import { computed } from 'vue'
import type { RouteLocationRaw, Router } from 'vue-router'
import type {
  LocationRoutes,
  UpdateLocationOnRestore,
  UpdateLocationOnUpdate,
  UpdateLocationPitcherEvent,
} from '@sdk/interfaces'
import type { RegisterLocation } from './useLocationManipulation'

export interface UseLocationAPI {
  app: string
  isRoot: boolean
  router: Router
  urlMode: 'hash' | 'history'
  sendToRoot: (body: UpdateLocationPitcherEvent) => Promise<any>
}

export default function useLocationAPI(params: UseLocationAPI) {
  const internalRouter = {
    path: computed(() => params.router.currentRoute.value.path),
    query: computed(() => params.router.currentRoute.value.query),
    push: (to: RouteLocationRaw) => params.router.push(to),
    replace: (to: RouteLocationRaw) => params.router.replace(to),
  }

  const getHashUrl = () => window.location.hash.slice(1).split('?')

  const getSearchParams = (search: URLSearchParams) => {
    Array.from(search.entries()).forEach(([key, value]) => {
      if (value === 'null') {
        search.set(key, '')
      }
    })

    return search
  }

  const globalRouter = {
    getPath: () => (params.urlMode === 'history' ? window.location.pathname : (getHashUrl()[0] ?? '')),
    getSearch: () => (params.urlMode === 'history' ? window.location.search : (getHashUrl()[1] ?? '')),
    getPathWithSearch: () => globalRouter.getPath() + (globalRouter.getSearch() ? `?${globalRouter.getSearch()}` : ''),
    getSearchAsObject: () => {
      const search = globalRouter.getSearch()

      if (!search) {
        return {}
      }

      return Object.fromEntries(getSearchParams(new URLSearchParams(search)))
    },
  }

  const buildUrl = (url: string, query: Record<string, any>) => {
    const search = getSearchParams(new URLSearchParams(query)).toString()

    if (!search) return url

    return `${url}?${search}`
  }

  const broadcast = {
    unregister: (appName: string) =>
      params.sendToRoot({
        action: 'unregister',
        app: appName,
      }),
    register: ({ child_app, parent_app = params.app, path }: RegisterLocation) =>
      params.sendToRoot({
        action: 'register',
        child_app: child_app,
        parent_app: parent_app,
        path,
      }),
    update: (routes: UpdateLocationOnUpdate['routes']) =>
      params.sendToRoot({
        action: 'update',
        routes,
      }),
    mount: () => {
      if (params.isRoot) {
        return broadcast.mounted()
      }

      return broadcast.getRoutesRequest()
    },
    mounted: () => params.sendToRoot({ action: 'mounted', app: params.app }),
    restore: (routes: UpdateLocationOnRestore['routes']) =>
      params.sendToRoot({
        action: 'restore',
        routes,
      }),
    restoreCompleted: (path: string, query: Record<string, any>) =>
      params.sendToRoot({
        action: 'restore_completed',
        app: params.app,
        path,
        query,
      }),
    getRoutesRequest: () =>
      params.sendToRoot({
        action: 'get_routes_request',
        app: params.app,
      }),
    getRoutesResponse: (app: string, routes: LocationRoutes) =>
      params.sendToRoot({
        action: 'get_routes_response',
        app,
        routes,
      }),
  }

  return {
    broadcast,
    internalRouter,
    globalRouter,
    buildUrl,
  }
}
